import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Container } from '../../components/Grid/Container';
import { Typography } from '../../components/Typography';
import { Card } from '../../components/Card/Card';
import { useGTM } from '../../hooks/useGTM';

const PortfolioSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xl} 0;
  background: ${({ theme }) => theme.colors.background.dark};
  min-height: calc(100vh - 200px);
`;

const FilterContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  flex-wrap: wrap;
  justify-content: center;
`;

const FilterButton = styled.button<{ $active?: boolean }>`
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primary.main : 'transparent'};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.text.primary : theme.colors.text.secondary};
  border: 1px solid ${({ theme }) => theme.colors.primary.main};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};

  &:hover {
    background: ${({ theme, $active }) =>
      $active ? theme.colors.primary.dark : theme.colors.primary.main}20;
  }
`;

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
  margin-top: ${({ theme }) => theme.spacing.xl};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.lg};
  }
`;

const ProjectCard = styled(Card)`
  overflow: hidden;
  padding: 0;
`;

const ProjectImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  transition: transform 0.3s ease;

  ${ProjectCard}:hover & {
    transform: scale(1.05);
  }
`;

const ProjectInfo = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
`;

const TagContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const Tag = styled.span`
  background: ${({ theme }) => theme.colors.primary.main}20;
  color: ${({ theme }) => theme.colors.primary.main};
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.sizes.sm};
`;

interface Project {
  id: number;
  title: string;
  description: string;
  image: string;
  category: string;
  tags: string[];
}

const projects: Project[] = [
  {
    id: 1,
    title: "E-commerce Tech Store",
    description: "Plataforma de e-commerce completa com sistema de pagamentos integrado.",
    image: "/images/projects/ecommerce.svg",
    category: "Web",
    tags: ["React", "Node.js", "MongoDB"]
  },
  {
    id: 2,
    title: "App Delivery",
    description: "Aplicativo de delivery com rastreamento em tempo real.",
    image: "/images/projects/delivery.svg",
    category: "Mobile",
    tags: ["React Native", "Firebase", "Google Maps"]
  },
  {
    id: 3,
    title: "Dashboard Analytics",
    description: "Dashboard para análise de dados em tempo real.",
    image: "/images/projects/dashboard.svg",
    category: "Web",
    tags: ["Vue.js", "D3.js", "AWS"]
  },
  // Adicione mais projetos conforme necessário
];

const categories = ["Todos", "Web", "Mobile", "Design", "Consultoria"];

export const Portfolio = () => {
  const { pushEvent } = useGTM();
  const [activeCategory, setActiveCategory] = useState("Todos");

  useEffect(() => {
    pushEvent('page_view', {
      page_title: 'Portfolio',
      page_path: '/portfolio',
      page_name: 'portfolio'
    });
  }, []);

  const handleProjectClick = (project: string) => {
    pushEvent('project_click', {
      project_name: project,
      page: 'portfolio'
    });
  };

  const filteredProjects = projects.filter(project => 
    activeCategory === "Todos" ? true : project.category === activeCategory
  );

  return (
    <PortfolioSection>
      <Container>
        <Typography $variant="h2" $gradient $align="center">
          Nosso Portfólio
        </Typography>
        <Typography $variant="body1" $align="center" $color="secondary">
          Conheça alguns dos projetos que desenvolvemos
        </Typography>

        <FilterContainer>
          {categories.map(category => (
            <FilterButton
              key={category}
              $active={activeCategory === category}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </FilterButton>
          ))}
        </FilterContainer>

        <ProjectGrid>
          {filteredProjects.map(project => (
            <ProjectCard key={project.id} $variant="glass" $hoverable>
              <ProjectImage src={project.image} alt={project.title} />
              <ProjectInfo>
                <Typography $variant="h4">{project.title}</Typography>
                <Typography $variant="body2" $color="secondary">
                  {project.description}
                </Typography>
                <TagContainer>
                  {project.tags.map(tag => (
                    <Tag key={tag}>{tag}</Tag>
                  ))}
                </TagContainer>
              </ProjectInfo>
            </ProjectCard>
          ))}
        </ProjectGrid>
      </Container>
    </PortfolioSection>
  );
}; 